import React from 'react'
import Loadable from 'react-loadable'
import { filter, map, uniqBy, get, omit, find, flatten, castArray, startCase } from 'lodash'
import { Tabs, Tab } from 'react-bootstrap'
import { arrayToOptions, objectToOptions } from 'Utilities/selectOptions'

import ActiveDirectoryTable from './TerminalServers/ActiveDirectoryTable'
import RDSTable from './TerminalServers/RDSTable'
import { SoftwareFilters } from './TerminalServers/Filters'
import SqlSplaTable from './Applications/SqlSplaTable'
import { defaultFilters, defaultApplicationFilters, ApplicationFilters } from './Filters'
import { filteredVMData, filteredSoftwareData } from './Data'
import SoftwareReportContext from './SoftwareReportContext'

const Loading = () => <div>Loading...</div>
const OperatingSystemSummary = Loadable({
  loader: () => import('./OperatingSystems/Summary'),
  loading: Loading
})

const ApplicationSummary = Loadable({
  loader: () => import('./Applications/Summary'),
  loading: Loading
})

class CloudCoreSoftwareReport extends React.Component {
  constructor(props) {
    super(props)
    this.defaultFilters = defaultApplicationFilters
    this.state = {
      filters: this.defaultFilters,
    }
  }

  resetFilters = () => this.setState({ filters: this.defaultFilters })
  changeFilters = (filters) => this.setState({ filters })
  showNonSummarisedData = (filter, value) => {
    if(filter == 'all') { this.toggleSummary(false) }

    const { filters } = this.state
    const { vms } = this.props
    const summarisedOptions = get(this.setFilterOptions(vms), filter, [])
    const clearedFilters = omit({ customers: 'All', operating_systems: 'All', vendors: 'All' }, filter)
    const chosenOption = find(summarisedOptions, ['label', value])

    this.setState({
      filters: { ...filters, [filter]: [chosenOption], ...clearedFilters, groupByField: 'none', showSummary: false }
    })
  }

  setFilterOptions = (vms) => {
    const { licensableSoftware, licensableSoftwareGroups, software, sqlSoftware } = this.props
    const softwareForFilters = [...castArray(software), ...castArray(sqlSoftware)]

    return ({
      clusters: objectToOptions(vms, 'cluster_name'),
      customers: objectToOptions(flatten([sqlSoftware, vms]), 'viaje_client_id', 'customer_name'),
      locations: uniqBy(map(vms, vm => ({ value: vm.location_id, label: vm.location_name })), 'label'),
      operating_systems: objectToOptions(vms, 'operating_system'),
      machine_type_sources: objectToOptions(vms, 'machine_type_source'),
      vendors: objectToOptions(vms, 'vendor'),
      editions: objectToOptions(vms, 'operating_system_edition'),
      functions: objectToOptions(map(vms, 'cluster'), 'function'),
      ownerships: objectToOptions(map(vms, 'cluster'), 'ownership'),
      licensableSoftware: arrayToOptions(licensableSoftware),
      licensableSoftwareGroups: arrayToOptions(licensableSoftwareGroups),
      applicationEditions: arrayToOptions(map(softwareForFilters, 'edition')),
      platformTypes: arrayToOptions(map(flatten([softwareForFilters]), 'platform_type')),
      regions: arrayToOptions(flatten([map(softwareForFilters, 'region_name'), map(vms, 'region_name')])),
    })
  }

  setSqlSoftwareFilterOptions = () => {
    const { licensableSoftware, licensableSoftwareGroups, sqlSoftware } = this.props

    return ({
      licensableSoftware: arrayToOptions(licensableSoftware),
      licensableSoftwareGroups: arrayToOptions(licensableSoftwareGroups),
      customers: objectToOptions(sqlSoftware, 'viaje_client_id', 'customer_name'),
      applicationEditions: arrayToOptions(map(sqlSoftware, 'edition')),
      platformTypes: arrayToOptions(map(sqlSoftware, 'platform_type')),
      regions: arrayToOptions(map(sqlSoftware, 'region_name')),
    })
  }

  setTSFilterOptions = () => {
    const { terminalServers } = this.props

    return ({
      customers: objectToOptions(terminalServers, 'viaje_client_id', 'customer_name'),
      regions: arrayToOptions(map(terminalServers, 'region_name')),
      platformTypes: arrayToOptions(map(terminalServers, 'platform_type')),
      customersStatus: arrayToOptions(['live', 'historic', 'pending offboarding'], startCase)
    })
  }

  toggleSummary = (showSummary = true, currentView) => {
    const filters = get({
      applications: defaultApplicationFilters
    }, currentView, defaultFilters)

    this.setState({ filters: { ...filters, showSummary } })
  }

  changeTab = (currentTab, changeFilters) => {
    const groupByField = get({
      'ms-Datacenter': 'Cluster',
      'ms-operatingSystems': 'Customer',
      'ms-applications': 'Customer',
      'SQL': 'Customer',
      'all': 'OperatingSystem'
    }, currentTab, this.state.groupByField)

    changeFilters({ ...defaultFilters, currentTab, groupByField })
  }

  render() {
    const { changeFilters, showNonSummarisedData, setFilterOptions, toggleSummary, changeTab, setSqlSoftwareFilterOptions, setTSFilterOptions } = this
    const { filters } = this.state
    const { vms, sqlSoftware, operating_systems, terminalServers, applications } = this.props
    const filterOptions = setFilterOptions(vms)

    return (
      <SoftwareReportContext.Provider value={{ filterOptions, filters, changeFilters, showNonSummarisedData, toggleSummary } }>
        <Tabs id="cloudcore-software-reports" activeKey={filters.currentTab} onSelect={currentTab => changeTab(currentTab, changeFilters)}>
          {/* <Tab eventKey='ms-Datacenter' title='Datacenter'>
            {filters.currentTab === 'ms-Datacenter' && (
              <OperatingSystemSummary data={flatten(filteredVMData({ vms, filters }))} />
            )}
          </Tab>

          <Tab eventKey='ms-operatingSystems' title='MS Operating Systems'>
            {filters.currentTab === 'ms-operatingSystems' && <OperatingSystemSummary data={flatten(filteredVMData({ vms: operating_systems, filters }))} />}
          </Tab>

          <Tab eventKey='ms-applications' title='MS Applications'>
            {filters.currentTab === 'ms-applications' && <ApplicationSummary data={software} />}
          </Tab> */}

          <Tab eventKey='SQL' title='SQL'>
            <ApplicationSummary data={sqlSoftware} />
          </Tab>

          <Tab eventKey='SQLSPLA' title='SQL SPLA'>
            <ApplicationFilters {...{ filters, filterOptions: setSqlSoftwareFilterOptions(), needsGroupBy: false, changeFilters, className: 'mt-3' }}/>

            <SqlSplaTable data={filteredSoftwareData(filters, sqlSoftware)} />
          </Tab>

          <Tab eventKey='ActiveDirectory' title='Active Directory'>
            <SoftwareFilters {...{ filters, filterOptions: setTSFilterOptions(), changeFilters, className: 'mt-3' }} />

            <ActiveDirectoryTable {...{ data: filteredSoftwareData(filters, filter(terminalServers, ['ad_server', 1])) }} />
          </Tab>

          <Tab eventKey='RDS' title='RDS'>
            <SoftwareFilters {...{ filters, filterOptions: setTSFilterOptions(), changeFilters, className: 'mt-3' }} />

            <RDSTable {...{ data: filteredSoftwareData(filters, terminalServers) }} />
          </Tab>
        </Tabs>
      </SoftwareReportContext.Provider>
    )
  }
}

export default CloudCoreSoftwareReport
